import React, { useState } from "react";
import { Folder, File as FileIcon, Loader2 } from 'lucide-react';
import { UIRoomItem } from "../../../models/RoomItem";
import { Util } from "../../../services/Utils/Util";
import { downloadFile } from "../../../services/Utils/UtilDownload";
import { Checkbox } from "../../../components/ui/checkbox";
import { Button } from "../../../components/ui/button";
import { cn } from "../../../lib/utils";
import { Fire } from "../../../services/FireClient";
import { useToast } from "../../../components/ui/use-toast";
import { t } from "@lingui/macro";
import { getDisplayName } from "../../../services/Utils/userDisplayName";

import { ItemPopover } from "../../components/ItemPopover";
import { DocumentViewerModal, isViewerSupported } from "../../components/DocumentViewerModal";
import { IconMoreHorizontal } from '../../components/FigmaIcons';
import { UserAvatar } from '../../components/UserAvatar';

interface FileGridItemProps {
    uitem: UIRoomItem;
    checked?: boolean;
    onCheckedChange?: (checked: boolean) => void;
    onMoveFolder?: () => void;
    onPreview?: (url: string) => void;
    canEdit?: boolean;
}

// 파일 확장자 추출 (최대 4글자)
function getFileExtension(filename: string | undefined): string {
    if (!filename) return '';
    const parts = filename.split('.');
    if (parts.length < 2) return '';
    const ext = parts[parts.length - 1].toUpperCase();
    return ext.length <= 4 ? ext : ext.substring(0, 4);
}

// 확장자별 배경색
function getExtensionColor(ext: string): string {
    const colors: { [key: string]: string } = {
        'PDF': 'bg-destructive',
        'DOC': 'bg-blue-500',
        'DOCX': 'bg-blue-500',
        'XLS': 'bg-green-600',
        'XLSX': 'bg-green-600',
        'PPT': 'bg-primary',
        'PPTX': 'bg-primary',
        'ZIP': 'bg-yellow-600',
        'RAR': 'bg-yellow-600',
        'TXT': 'bg-muted/500',
        'CSV': 'bg-green-500',
        'MP3': 'bg-purple-500',
        'MP4': 'bg-pink-500',
        'MOV': 'bg-pink-500',
    };
    return colors[ext] || 'bg-muted-foreground';
}

export const FileGridItem: React.FC<FileGridItemProps> = ({ uitem, checked, onCheckedChange, onMoveFolder, onPreview, canEdit }) => {
    const item = uitem.item;
    const isFolder = item.type === 'folder';
    const isMessageFolder = (uitem.itemType as string) === 'MessageFolder';
    const isImage = item.url && Util.isImageUrl(item.url);
    const ext = getFileExtension(item.title || item.file?.filename);
    const extColor = getExtensionColor(ext);
    const { toast } = useToast();
    const [isOpening, setIsOpening] = useState(false);
    const viewerSupported = !isFolder && isViewerSupported(item.title || item.file?.filename || '');
    const [docViewerOpen, setDocViewerOpen] = useState(false);
    const [docViewerUrl, setDocViewerUrl] = useState('');

    const handleOpen = async () => {
        if (isFolder) {
            onMoveFolder?.();
            return;
        }

        // CrossViewer 지원 파일이면 뷰어로 열기
        if (viewerSupported) {
            setIsOpening(true);
            try {
                const res = await Fire.item.getRoomItemDownloadURL(item.roomId, item.id);
                if (res.status === 'SUCCESS' && res.url) {
                    setDocViewerUrl(res.url);
                    setDocViewerOpen(true);
                } else {
                    toast({
                        variant: "destructive",
                        title: t`Error`,
                        description: res.status === 'NOT_MEMBER' ? t`You don't have permission.` : t`File not found or expired.`,
                    });
                }
            } catch (error) {
                console.error("Failed to get download URL", error);
            } finally {
                setIsOpening(false);
            }
            return;
        }

        setIsOpening(true);
        try {
            const res = await Fire.item.getRoomItemDownloadURL(item.roomId, item.id);
            if (res.status === 'SUCCESS' && res.url) {
                await downloadFile(res.url, item.title || item.file?.filename || '');
            } else {
                toast({
                    variant: "destructive",
                    title: t`Download Failed`,
                    description: res.status === 'NOT_MEMBER' ? t`You don't have permission.` : t`File not found or expired.`,
                });
            }
        } catch (error) {
            console.error("Failed to get download URL", error);
            toast({
                variant: "destructive",
                title: t`Error`,
                description: (error as Error).message,
            });
        } finally {
            setIsOpening(false);
        }
    };

    return (
        <>
        <div className="relative group bg-background border border-border rounded-panel overflow-hidden shadow-[0px_4px_6px_0px_rgba(0,0,0,0.02)] p-1">
            <div className="absolute top-[11px] left-[11px] z-10">
                <Checkbox
                    checked={checked}
                    onCheckedChange={onCheckedChange}
                    disabled={isMessageFolder}
                    className="w-5 h-5 border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                />
            </div>

            <div
                className={cn("h-[130px] flex items-center justify-center bg-muted rounded-t-xl cursor-pointer overflow-hidden relative", isFolder && "text-primary")}
                onClick={handleOpen}
            >
                {isFolder ? (
                    <Folder className="w-16 h-16 fill-current" />
                ) : isImage ? (
                    <img src={item.url} className="w-full h-full object-contain transition-transform group-hover:scale-105" alt={item.title} />
                ) : (
                    <div className="relative flex flex-col items-center justify-center">
                        <FileIcon className="w-9 h-9 text-muted-foreground/30" />
                        {ext && (
                            <span className={cn(
                                "absolute bottom-2 px-2 py-0.5 text-meta-sm font-bold text-on-media-text rounded",
                                extColor
                            )}>
                                {ext}
                            </span>
                        )}
                    </div>
                )}
                {isOpening && (
                    <div className="absolute inset-0 bg-background/60 flex items-center justify-center">
                        <Loader2 className="w-10 h-10 animate-spin text-primary" />
                    </div>
                )}
            </div>

            <div className="pb-2 pt-3 px-3">
                <div
                    className="text-sm text-foreground truncate mb-1 cursor-pointer hover:text-primary leading-4"
                    onClick={handleOpen}
                >
                    {item.title || item.url}
                </div>
                {!isMessageFolder && (
                    <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-1.5 overflow-hidden">
                            <UserAvatar src={item.user.photoUrl} name={getDisplayName(item.user)} className="w-5 h-5" />
                            <span className="text-xs text-label truncate">{getDisplayName(item.user)}</span>
                        </div>
                        <div className="shrink-0 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 transition-opacity">
                            <ItemPopover item={item} canEdit={!!canEdit}>
                                <Button variant="ghost" size="icon" className="w-7 h-7 rounded-full" onClick={(e) => e.stopPropagation()}>
                                    <IconMoreHorizontal className="w-4 h-4 text-muted-foreground" />
                                </Button>
                            </ItemPopover>
                        </div>
                    </div>
                )}
            </div>
        </div>
        {docViewerOpen && (
            <DocumentViewerModal
                url={docViewerUrl}
                title={item.title || item.file?.filename}
                onClose={() => setDocViewerOpen(false)}
            />
        )}
        </>
    );
};
